import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import { Star, Search, Trash2, Archive, Eye, TrendingUp, DollarSign, Package, Plus, RefreshCw } from 'lucide-react';

// Use environment variable for preview, or current origin for production
const getApiUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  const hostname = window.location.hostname || '';
  if (hostname.includes('emergent.host') || hostname.includes('emergent-')) {
    return window.location.origin;
  }
  return envUrl || window.location.origin;
};
const API = getApiUrl();

const SpecialProducts = () => {
  const { user } = useAuth();
  const { currentBranch, isMatriz } = useBranch();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, [showArchived, currentBranch]);

  const fetchProducts = async () => {
    try {
      // Build branch filter
      let url = `${API}/api/special-products/?include_archived=${showArchived}`;
      if (currentBranch) {
        url += `&branch_id=${currentBranch.branch_id}&is_matriz=${isMatriz}`;
      }
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      }
    } catch (err) {
      toast.error('Error al cargar productos especiales');
    } finally {
      setLoading(false);
    }
  };

  const archiveProduct = async (productId) => {
    try {
      const res = await fetch(`${API}/api/special-products/${productId}/archive`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        toast.success('Producto archivado');
        fetchProducts();
      }
    } catch (err) {
      toast.error('Error al archivar');
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('¿Eliminar este producto? Solo se pueden eliminar productos que no se han vendido.')) return;
    
    try {
      const res = await fetch(`${API}/api/special-products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        toast.success('Producto eliminado');
        fetchProducts();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'No se puede eliminar');
      }
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate totals
  const totalRevenue = products.reduce((sum, p) => sum + (p.total_revenue || 0), 0);
  const totalSold = products.reduce((sum, p) => sum + (p.times_sold || 0), 0);
  const avgProfit = products.length > 0 
    ? products.reduce((sum, p) => sum + (p.margin_percent || 0), 0) / products.length 
    : 0;

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Star className="w-6 h-6 text-purple-500" />
                Productos Especiales
              </h1>
              <p className="text-gray-500 text-sm">
                Productos personalizados creados en el POS
              </p>
            </div>
            <Button onClick={fetchProducts} variant="outline" className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{products.length}</div>
                  <div className="text-xs text-gray-500">Productos Creados</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{totalSold}</div>
                  <div className="text-xs text-gray-500">Veces Vendido</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">${totalRevenue.toFixed(2)}</div>
                  <div className="text-xs text-gray-500">Ingresos Totales</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Star className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{avgProfit.toFixed(1)}%</div>
                  <div className="text-xs text-gray-500">Margen Promedio</div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg p-4 shadow mb-6">
            <div className="flex gap-4 items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar producto..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="w-4 h-4"
                />
                Mostrar archivados
              </label>
            </div>
          </div>

          {/* Products Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-4 font-medium text-gray-600">Producto</th>
                  <th className="text-right p-4 font-medium text-gray-600">Precio</th>
                  <th className="text-right p-4 font-medium text-gray-600">Costo</th>
                  <th className="text-right p-4 font-medium text-gray-600">Ganancia</th>
                  <th className="text-center p-4 font-medium text-gray-600">Vendidos</th>
                  <th className="text-right p-4 font-medium text-gray-600">Ingresos</th>
                  <th className="text-center p-4 font-medium text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center p-8 text-gray-400">
                      Cargando...
                    </td>
                  </tr>
                ) : filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center p-8 text-gray-400">
                      <Star className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>No hay productos especiales</p>
                      <p className="text-xs mt-1">Crea uno desde el POS con el botón "+ Personalizado"</p>
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map(product => (
                    <tr key={product.special_product_id} className={`hover:bg-gray-50 ${product.archived ? 'opacity-50' : ''}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Star className={`w-4 h-4 ${product.saved_to_catalog ? 'text-yellow-500' : 'text-gray-300'}`} />
                          <div>
                            <div className="font-medium">{product.name}</div>
                            <div className="text-xs text-gray-500">
                              {product.ingredients?.length || 0} ingredientes
                              {product.notes && ` • ${product.notes}`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-right font-medium">${product.price?.toFixed(2)}</td>
                      <td className="p-4 text-right text-red-600">${product.cost?.toFixed(2)}</td>
                      <td className="p-4 text-right">
                        <span className="text-green-600 font-medium">${product.profit?.toFixed(2)}</span>
                        <span className="text-xs text-gray-500 ml-1">({product.margin_percent?.toFixed(1)}%)</span>
                      </td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${product.times_sold > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {product.times_sold || 0}
                        </span>
                      </td>
                      <td className="p-4 text-right font-medium text-blue-600">
                        ${(product.total_revenue || 0).toFixed(2)}
                      </td>
                      <td className="p-4">
                        <div className="flex justify-center gap-1">
                          <button
                            onClick={() => setSelectedProduct(product)}
                            className="p-2 hover:bg-gray-100 rounded"
                            title="Ver detalles"
                          >
                            <Eye className="w-4 h-4 text-gray-500" />
                          </button>
                          {!product.archived && (
                            <button
                              onClick={() => archiveProduct(product.special_product_id)}
                              className="p-2 hover:bg-gray-100 rounded"
                              title="Archivar"
                            >
                              <Archive className="w-4 h-4 text-gray-500" />
                            </button>
                          )}
                          {product.times_sold === 0 && (
                            <button
                              onClick={() => deleteProduct(product.special_product_id)}
                              className="p-2 hover:bg-red-100 rounded"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="bg-purple-600 text-white p-4 rounded-t-lg flex justify-between items-center">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Star className="w-5 h-5" />
                {selectedProduct.name}
              </h2>
              <button onClick={() => setSelectedProduct(null)} className="hover:bg-purple-700 p-1 rounded">
                ✕
              </button>
            </div>
            <div className="p-4">
              {/* Pricing Info */}
              <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                <div className="bg-gray-50 rounded p-3">
                  <div className="text-xs text-gray-500">Precio</div>
                  <div className="text-lg font-bold">${selectedProduct.price?.toFixed(2)}</div>
                </div>
                <div className="bg-red-50 rounded p-3">
                  <div className="text-xs text-gray-500">Costo</div>
                  <div className="text-lg font-bold text-red-600">${selectedProduct.cost?.toFixed(2)}</div>
                </div>
                <div className="bg-green-50 rounded p-3">
                  <div className="text-xs text-gray-500">Ganancia</div>
                  <div className="text-lg font-bold text-green-600">${selectedProduct.profit?.toFixed(2)}</div>
                </div>
              </div>

              {/* Ingredients */}
              <div className="border rounded-lg overflow-hidden mb-4">
                <div className="bg-gray-100 p-2 font-medium text-sm">Ingredientes</div>
                <div className="divide-y">
                  {selectedProduct.ingredients?.map((ing, idx) => (
                    <div key={idx} className="p-2 flex justify-between text-sm">
                      <span>{ing.name} ({ing.quantity} {ing.unit})</span>
                      <span className="text-gray-500">${ing.cost?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Veces vendido:</span>
                  <span className="font-medium ml-2">{selectedProduct.times_sold || 0}</span>
                </div>
                <div>
                  <span className="text-gray-500">Ingresos totales:</span>
                  <span className="font-medium ml-2 text-blue-600">${(selectedProduct.total_revenue || 0).toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Creado:</span>
                  <span className="font-medium ml-2">{new Date(selectedProduct.created_at).toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="text-gray-500">Por:</span>
                  <span className="font-medium ml-2">{selectedProduct.created_by}</span>
                </div>
              </div>

              {selectedProduct.notes && (
                <div className="mt-4 p-3 bg-yellow-50 rounded text-sm">
                  <strong>Notas:</strong> {selectedProduct.notes}
                </div>
              )}
            </div>
            <div className="border-t p-4 flex justify-end">
              <Button onClick={() => setSelectedProduct(null)}>Cerrar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialProducts;
